/**
 * HOF-215 AC 6 + AC 9 — what a match group says about its own health.
 *
 * ``degraded_reasons`` has been on the fact since HOF-90 and was mapped
 * NOWHERE: 38 Wiesenhof groups were known-degraded, counted, and invisible to
 * the person reconciling the month. Same for ``money.delivery_unpriced``, which
 * is the difference between "the buyer owes you EUR 35.332,15" and "this
 * Lieferschein prints no prices, so there is nothing to compare".
 *
 * The vocabulary is closed and the labels are German, because the operator is a
 * German farmer. An UNKNOWN reason renders its raw key rather than being
 * dropped: a marker this build does not recognise is still a marker, and
 * swallowing it would rebuild exactly the silence HOF-90 closed.
 */
import type { MatchGroup } from '../../../hooks/useMatchGroups';

export interface GroupHealthChip {
  /** Stable key for React and for tests. */
  id: string;
  label: string;
  /** Long form, shown as a tooltip. */
  title: string;
  tone: 'warning' | 'info';
}

const REASON_LABELS: Record<string, { label: string; title: string; tone: 'warning' | 'info' }> = {
  empty_line_pairs_with_linked_delivery: {
    label: 'Keine Positionen zugeordnet',
    title:
      'Lieferschein und Gutschrift liegen beide in dieser Gruppe, aber keine einzige Position wurde einander zugeordnet.',
    tone: 'warning',
  },
  status_edge_disagreement: {
    label: 'Status widerspricht der Verknüpfung',
    title:
      'Die Gruppe meldet ein fehlendes Dokument, obwohl die Verknüpfung zu genau diesem Dokument vorhanden ist.',
    tone: 'warning',
  },
  no_line_items_on_delivery: {
    label: 'Lieferschein ohne Positionen',
    title: 'Aus dem Lieferschein wurden keine Positionen ausgelesen — es gibt nichts zuzuordnen.',
    tone: 'info',
  },
  no_line_items_on_settlement: {
    label: 'Gutschrift ohne Warenpositionen',
    title: 'Aus der Gutschrift wurden keine Warenpositionen ausgelesen — es gibt nichts zuzuordnen.',
    tone: 'info',
  },
  quantities_irreconcilable: {
    label: 'Mengen passen nicht zusammen',
    title:
      'Beide Seiten haben Positionen, aber keine Lesart der Mengen ordnet sie einander zu. Prüfen Sie, ob der richtige Lieferschein verknüpft ist.',
    tone: 'warning',
  },
  delivery_lines_contested: {
    label: 'Lieferschein-Position umstritten',
    title:
      'Mehrere Gutschriften könnten dieselbe Lieferschein-Position abrechnen — die Mengen stimmen bei allen. Ohne weiteren Beleg lässt sich nicht entscheiden, welche Gutschrift diese Lieferung vergütet.',
    tone: 'warning',
  },
  reconciler_unavailable: {
    label: 'Noch nicht abgeglichen',
    title: 'Für diese Gruppe liegt kein Abgleich-Ergebnis vor.',
    tone: 'info',
  },
};

export const DELIVERY_UNPRICED_CHIP: GroupHealthChip = {
  id: 'delivery-unpriced',
  label: 'Lieferschein ohne Preise',
  title:
    'Der Lieferschein enthält keine Beträge, deshalb ist der Geldvergleich für diese Gruppe nicht aussagekräftig — die angezeigte Differenz entspricht der vollen Gutschrift.',
  tone: 'info',
};

/**
 * The chips to render on one group. Empty for a healthy group, which is the
 * common case and must stay visually silent.
 */
export function groupHealthChips(group: MatchGroup): GroupHealthChip[] {
  const chips: GroupHealthChip[] = [];
  if (group.money?.deliveryUnpriced) chips.push(DELIVERY_UNPRICED_CHIP);
  for (const reason of group.degradedReasons ?? []) {
    const known = REASON_LABELS[reason];
    chips.push(
      known
        ? { id: reason, label: known.label, title: known.title, tone: known.tone }
        : { id: reason, label: reason, title: reason, tone: 'info' },
    );
  }
  return chips;
}
